import { gql } from '@urql/core';
import { useAppState } from 'client/providers/AppProvider';
import React, { useCallback, useMemo } from 'react';
import {
    CheckFollowingStatusDocument,
    CheckFollowingStatusQuery,
    CheckFollowingStatusQueryVariables,
    Espin_Follow_Type_Enum,
    Espin_Role_Enum,
    FollowDocument,
    FollowMutation,
    FollowMutationVariables,
    HighSchoolProfileInfoFragment,
    UnfollowDocument,
    UnfollowMutation,
    UnfollowMutationVariables,
} from '../../../generated/spin-graphql';
import { PictureWithFallback } from '../../sections/PictureWithFallback';

import { HighSchoolSocialsDisplay } from '../HighSchoolSocialsDisplay';
import { HighSchoolInfoButtons } from './HighSchoolInfoButtons';
import Link from 'next/link';
import { MessageIcon } from '../../svg/MessageIcon';
import { useAuthenticatedMutation, useAuthenticatedQuery } from '../../../client/hooks';
import { PlainTextLocationDisplay } from '../../common/PlainTextLocationDisplay';

type Props = {
    highSchool: HighSchoolProfileInfoFragment;
};

gql`
    fragment HighSchoolProfileInfo on espin_high_school {
        id
        name
        profile_pic_file {
            ...FormFile
        }
        contact_email
        contact_name
        contact_phone
        address {
            ...PlainTextLocationDisplay
        }
        socials {
            facebook
            twitch
            twitter
            instagram
            discord
            twitch_channel {
                id
            }
            youtube_channel {
                id
            }
        }
        ...HighSchoolSocials
        ...HighSchoolInfoButtons
    }
`;

export const HighSchoolProfileInfo: React.FC<Props> = ({ highSchool }) => {
    const { user } = useAppState();

    const [_, follow] = useAuthenticatedMutation<FollowMutation, FollowMutationVariables>(FollowDocument);
    const [__, unFollow] = useAuthenticatedMutation<UnfollowMutation, UnfollowMutationVariables>(UnfollowDocument);

    const { isReady, data: followingStatus } = useAuthenticatedQuery<
        CheckFollowingStatusQuery,
        CheckFollowingStatusQueryVariables
    >(
        {
            query: CheckFollowingStatusDocument,
            variables: useMemo(
                () => ({
                    where: {
                        high_school_id: { _eq: highSchool?.id },
                        college_id: { _eq: user?.college_profile?.college?.id },
                        follow_type: { _eq: Espin_Follow_Type_Enum.CollegeFollowsHighSchool },
                    },
                }),
                [highSchool?.id, user?.college_profile?.college?.id],
            ),
            pause: !user || !user.college_profile?.college?.id,
        },
        ['espin_follow'],
    );
    const followHighSchool = useCallback(() => {
        follow(
            {
                input: {
                    college_id: user?.college_profile?.college?.id,
                    college_user_name: `${user?.college_profile?.first_name} ${user?.college_profile?.last_name}`,
                    high_school_id: highSchool?.id,
                    follow_type: Espin_Follow_Type_Enum.CollegeFollowsHighSchool,
                },
            },
            { additionalTypenames: ['espin_follow'] },
        );
    }, [
        user?.college_profile?.college?.id,
        highSchool?.id,
        follow,
        user?.college_profile?.first_name,
        user?.college_profile?.last_name,
    ]);

    const unfollowHighSchool = useCallback(() => {
        unFollow(
            {
                where: {
                    high_school_id: { _eq: highSchool?.id },
                    college_id: { _eq: user?.college_profile?.college?.id },
                    follow_type: { _eq: Espin_Follow_Type_Enum.CollegeFollowsHighSchool },
                },
            },
            { additionalTypenames: ['espin_follow'] },
        );
    }, [highSchool?.id, unFollow, user?.college_profile?.college?.id]);

    const canSeeFollowButton = useMemo(() => user?.role === Espin_Role_Enum.EspinCollegeUser, [user?.role]);

    const handleButton = useCallback(() => {
        if (followingStatus?.espin_follow.length <= 0) {
            followHighSchool();
        } else {
            unfollowHighSchool();
        }
    }, [followHighSchool, followingStatus?.espin_follow.length, unfollowHighSchool]);

    const hasPlayerInfoButtons =
        highSchool.contact_phone || highSchool.contact_email || highSchool.contact_name || highSchool.socials?.discord;
    const hasSocials =
        highSchool.socials?.facebook ||
        highSchool.socials?.twitter ||
        highSchool.socials?.instagram ||
        highSchool.socials?.twitch_channel ||
        highSchool.socials?.youtube_channel;

    return (
        <div className="high-school-profile-info">
            <div className="left-panel">
                <div className="high-school-logo-info-container">
                    <div className="high-school-profile-image">
                        <PictureWithFallback
                            icon={highSchool.profile_pic_file}
                            width={100}
                            height={100}
                            alt={`${highSchool.name}} profile picture`}
                        />
                    </div>
                    <div className="bio">
                        <h1 className="high-school-name">{highSchool.name}</h1>
                        {highSchool.address && (
                            <p className="high-school-location">
                                <PlainTextLocationDisplay address={highSchool.address} />
                            </p>
                        )}
                        {hasSocials && <HighSchoolSocialsDisplay socials={highSchool.socials} />}
                    </div>
                </div>
                {hasPlayerInfoButtons && <HighSchoolInfoButtons highSchool={highSchool} />}
            </div>
            <div className="right-panel">
                {canSeeFollowButton && (
                    <div className="social-controls-container">
                        <Link
                            href={
                                user.subscription_status.is_subscribed
                                    ? `/dashboard/messages/?high_school_slug=${highSchool.slug}`
                                    : `/pricing`
                            }
                        >
                            <a>
                                <button className="button circle-button" title="Send Message">
                                    <MessageIcon />
                                </button>
                            </a>
                        </Link>
                        {isReady && canSeeFollowButton && (
                            <button
                                onClick={handleButton}
                                className={`button medium-button${
                                    followingStatus?.espin_follow.length > 0 ? ' red-button' : ' blue-button'
                                }`}
                            >
                                {followingStatus?.espin_follow.length <= 0 ? 'Follow' : 'Unfollow'}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
